import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { StructuredLoggerService, LOGGING_SKIP_HEADER } from './structured-logger.service';

const SKIP_GLOBAL_HTTP_ERROR_DIALOG_HEADER = 'X-SKIP-GLOBAL-HTTP-ERROR-DIALOG';
const LEGACY_REQUEST_HEADERS = Object.freeze({
  'X-IMEI': '0',
  'X-MERCHANTID': '421423',
  'Content-Type': 'application/json',
});

function isAssetRequest(url: string): boolean {
  return url.startsWith('assets/') || url.includes('/assets/');
}

function stripInternalHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
  let headers = req.headers;
  if (headers.has(LOGGING_SKIP_HEADER)) {
    headers = headers.delete(LOGGING_SKIP_HEADER);
  }
  if (headers.has(SKIP_GLOBAL_HTTP_ERROR_DIALOG_HEADER)) {
    headers = headers.delete(SKIP_GLOBAL_HTTP_ERROR_DIALOG_HEADER);
  }
  return headers === req.headers ? req : req.clone({ headers });
}

function applyLegacyRequestHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
  let headers = req.headers;
  for (const [headerName, headerValue] of Object.entries(LEGACY_REQUEST_HEADERS)) {
    if (!headers.has(headerName)) {
      headers = headers.set(headerName, headerValue);
    }
  }

  return headers === req.headers ? req : req.clone({ headers });
}

function isHttpResponse(event: HttpEvent<unknown>): event is HttpResponse<unknown> {
  return event instanceof HttpResponse;
}

export const httpLoggingInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  if (isAssetRequest(request.url)) {
    return next(request);
  }

  const logger = inject(StructuredLoggerService);
  const requestWithLegacyHeaders = applyLegacyRequestHeaders(request);
  const shouldSkipLogging = requestWithLegacyHeaders.headers.has(LOGGING_SKIP_HEADER);
  const requestToSend = stripInternalHeaders(requestWithLegacyHeaders);

  const startedAt = Date.now();
  const correlationId = logger.createCorrelationId();

  return next(requestToSend).pipe(
    tap((event) => {
      if (!shouldSkipLogging && isHttpResponse(event)) {
        logger.logHttp({
          transport: 'angular-http',
          correlationId,
          success: true,
          method: requestToSend.method,
          url: requestToSend.urlWithParams,
          requestHeaders: logger.headersToObject(requestToSend.headers),
          requestBody: requestToSend.body,
          responseStatus: event.status,
          responseHeaders: logger.headersToObject(event.headers),
          responseBody: event.body,
          durationMs: Date.now() - startedAt,
        });
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (!shouldSkipLogging) {
        logger.logHttp({
          transport: 'angular-http',
          correlationId,
          success: false,
          method: requestToSend.method,
          url: requestToSend.urlWithParams,
          requestHeaders: logger.headersToObject(requestToSend.headers),
          requestBody: requestToSend.body,
          responseStatus: error?.status,
          responseHeaders: error?.headers ? logger.headersToObject(error.headers) : {},
          responseBody: error?.error,
          error: {
            message: error?.message,
            name: error?.name,
            statusText: error?.statusText,
          },
          durationMs: Date.now() - startedAt,
        });
        logger.logException(error, 'http-interceptor');
      }

      return throwError(() => error);
    })
  );
};
